import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { MagnifyingGlass, HardHat, User as UserIcon, Wrench, CalendarBlank, Files, X, Command } from "@phosphor-icons/react";
import { toast } from "sonner";

const KIND_META = {
  chantiers: { label: "Chantiers", icon: HardHat, color: "#005CFF", href: (i) => `/chantiers/${i.id}` },
  interventions: { label: "Interventions", icon: CalendarBlank, color: "#F59E0B", href: (i) => `/chantiers/${i.chantier_id || ""}` },
  clients: { label: "Clients", icon: UserIcon, color: "#10B981", href: (i) => `/chantiers?client=${i.id}` },
  equipment: { label: "Équipement", icon: Wrench, color: "#EF4444", href: (i) => `/equipment/${i.id}` },
  users: { label: "Équipe", icon: UserIcon, color: "#8B5CF6", href: () => "/admin" },
  docs: { label: "Documents", icon: Files, color: "#94A3B8", href: () => "/documents" },
};

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (open) {
      setQ(""); setResults(null); setSelectedIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/search", { params: { q, limit: 6 } });
        setResults(r.data);
        setSelectedIdx(0);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const flatList = () => {
    if (!results) return [];
    const out = [];
    Object.entries(KIND_META).forEach(([k, meta]) => {
      (results[k] || []).forEach((item) => out.push({ kind: k, meta, item }));
    });
    return out;
  };

  const goto = (entry) => {
    onClose();
    nav(entry.meta.href(entry.item));
  };

  const onKey = (e) => {
    const list = flatList();
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx((i) => Math.min(list.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (list[selectedIdx]) goto(list[selectedIdx]);
    }
  };

  if (!open) return null;

  const list = flatList();
  const totalHits = list.length;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-16 sm:pt-24 px-3" onClick={onClose} data-testid="global-search-overlay">
      <div className="w-full max-w-lg bg-neutral-950 border border-white/15 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-3 py-3 border-b border-white/10">
          <MagnifyingGlass size={16} className="text-white/50" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Chercher chantier, client, intervention, outil…"
            className="flex-1 bg-transparent outline-none text-sm placeholder-white/30"
            data-testid="global-search-input"
          />
          {loading && <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />}
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="global-search-close"><X size={14} /></button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {!q.trim() && (
            <div className="text-center py-8 text-white/40 text-xs">
              <Command size={20} className="mx-auto mb-2 opacity-50" />
              Recherche instantanée dans BKflow
              <div className="mt-2 text-[10px]">↑↓ pour naviguer · Entrée pour ouvrir · Échap pour fermer</div>
            </div>
          )}
          {q.trim() && results && totalHits === 0 && !loading && (
            <div className="text-center py-8 text-white/40 text-xs">Aucun résultat pour « {q} »</div>
          )}
          {q.trim() && results && Object.entries(KIND_META).map(([k, meta]) => {
            const items = (results[k] || []);
            if (items.length === 0) return null;
            const IconC = meta.icon;
            return (
              <div key={k} className="p-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 px-2 py-1">{meta.label}</div>
                <div className="space-y-0.5">
                  {items.map((it) => {
                    const globalIdx = list.findIndex((x) => x.kind === k && x.item.id === it.id);
                    const isSel = globalIdx === selectedIdx;
                    return (
                      <button
                        key={it.id}
                        onClick={() => goto({ kind: k, meta, item: it })}
                        onMouseEnter={() => setSelectedIdx(globalIdx)}
                        className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left ${isSel ? "bg-[#005CFF]/30" : "hover:bg-white/5"}`}
                        data-testid={`search-result-${k}-${it.id}`}
                      >
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.color + "22", color: meta.color }}>
                          <IconC size={14} weight="duotone" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{it.name || it.title || it.email || "—"}</div>
                          <div className="text-[10px] text-white/40 truncate">
                            {it.client_name || it.email || it.phone || it.brand || it.address || it.category || ""}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
